import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { CentsAmount } from '@payer-admin-ui/api/src/external/java/CentsAmount';
import { InvoicePageResponse, Summary } from '@payer-admin-ui/api/src/invoices/responses/InvoiceResponseV3';
import { PaymentStatus } from '@payer-admin-ui/api/src/payments/models/PaymentStatus';
import { getLastMonthDateRange } from '@payer-admin-ui/components-v2/src/utils/dates';
import { InvoiceStatus } from '@payer-admin-ui/api/src/invoices/models/InvoiceStatus';
import { InvoiceType } from '@payer-admin-ui/api/src/invoices/models/InvoiceType';
import { invoiceAPI } from '../../../redux/invoiceAPI';
import { paymentAPI } from '../../../redux/paymentAPI';
import { bankFilePaymentsAPI, BankFilePaymentsSumByCurrency } from '../../../redux/bankFilePaymentsAPI';
import { MerchantAdminState } from '../../../redux/store';
import { emptyInvoiceFilters } from '../invoices/invoicesGlobalState';
import { emptyPaymentFilters } from '../payments/paymentsGlobalState';

export type OverdueUnpaidInvoicesWidgetState = {
  isFetching: boolean;
  error: unknown | null;
  totalCount: number | null;
  summary?: Partial<Record<CurrencyCode, Summary>>;
};

export type SettledPaymentsSummary = {
  totalAmount: CentsAmount;
  count: number;
};

export type SettledPaymentsWidgetState = {
  isFetching: boolean;
  error: unknown | null;
  totalCount: number | null;
  summaryByCurrency: Partial<Record<CurrencyCode, SettledPaymentsSummary>>;
};

export type UnmappedBankFilePaymentsWidgetState = {
  isFetching: boolean;
  error: unknown | null;
  totalCount: number | null;
  summary: BankFilePaymentsSumByCurrency[];
};

export type DashboardWidgetsState = {
  overdueUnpaidInvoices: OverdueUnpaidInvoicesWidgetState;
  settledPayments: SettledPaymentsWidgetState;
  unmappedBankFilePayments: UnmappedBankFilePaymentsWidgetState;
};

export const fetchOverdueUnpaidInvoicesWidget = createAsyncThunk<InvoicePageResponse, { dueDateBefore: string }>(
  'dashboardWidgets/fetchOverdueUnpaidInvoicesWidget',
  async ({ dueDateBefore }) => {
    return invoiceAPI.fetchPage({
      page: 0,
      pageSize: 1,
      overdue: true,
      sort: [],
      filters: {
        ...emptyInvoiceFilters,
        invoiceStatus: [InvoiceStatus.NEW, InvoiceStatus.FINAL],
        invoiceType: [InvoiceType.NORMAL, InvoiceType.ESCROW, InvoiceType.PREPAYMENT],
        dueDateBefore,
      },
    });
  },
);

export type SettledPaymentsResponse = {
  totalCount: number;
  summaryByCurrency: Partial<Record<CurrencyCode, SettledPaymentsSummary>>;
};

export const fetchSettledPaymentsWidget = createAsyncThunk<SettledPaymentsResponse>(
  'dashboardWidgets/fetchSettledPaymentsWidget',
  async () => {
    const { creationDateStart, creationDateEnd } = getLastMonthDateRange();
    const response = await paymentAPI.fetchPaymentsSummary({
      filters: {
        ...emptyPaymentFilters,
        statuses: [PaymentStatus.FULLY_CAPTURED],
        creationDateStart,
        creationDateEnd,
      },
    });
    return {
      totalCount: response.totalCount,
      summaryByCurrency: response.summaryByCurrency ?? {},
    };
  },
);

export type UnmappedBankFilePaymentsResponse = {
  totalCount: number;
  summary: BankFilePaymentsSumByCurrency[];
};

export const fetchUnmappedBankFilePaymentsWidget = createAsyncThunk<UnmappedBankFilePaymentsResponse>(
  'dashboardWidgets/fetchUnmappedBankFilePaymentsWidget',
  async () => {
    const response = await bankFilePaymentsAPI.bankFilePayments.fetchPage({
      page: 0,
      pageSize: 1,
      filters: {
        fullyMapped: ['false'],
        doNotMap: ['false'],
        toPayer: ['false'],
        bankAccountTypes: [],
        processingDateStart: '',
        processingDateEnd: '',
        bookingDateStart: '',
        bookingDateEnd: '',
        name: '',
        id: '',
        manuallyMapped: null,
        search: '',
        doNotMapReasons: [],
        customer: '',
        fundSourceTypes: ['PAYMENT_TO_ACCOUNT'],
      },
      sort: [],
    });

    return {
      totalCount: response.totalCount,
      summary: response.summaryByCurrency ?? [],
    };
  },
);

const initialState: DashboardWidgetsState = {
  overdueUnpaidInvoices: {
    isFetching: false,
    error: null,
    totalCount: null,
    summary: undefined,
  },
  settledPayments: {
    isFetching: false,
    error: null,
    totalCount: null,
    summaryByCurrency: {},
  },
  unmappedBankFilePayments: {
    isFetching: false,
    error: null,
    totalCount: null,
    summary: [],
  },
};

const dashboardWidgetsSlice = createSlice({
  name: 'dashboardWidgets',
  initialState,
  reducers: {
    resetOverdueUnpaidInvoicesWidgetError: state => {
      state.overdueUnpaidInvoices.error = null;
    },
    resetSettledPaymentsWidgetError: state => {
      state.settledPayments.error = null;
    },
    resetUnmappedBankFilePaymentsWidgetError: state => {
      state.unmappedBankFilePayments.error = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchOverdueUnpaidInvoicesWidget.pending, state => {
      state.overdueUnpaidInvoices = {
        ...state.overdueUnpaidInvoices,
        isFetching: true,
        error: null,
      };
    });

    builder.addCase(fetchOverdueUnpaidInvoicesWidget.fulfilled, (state, { payload }) => {
      state.overdueUnpaidInvoices = {
        isFetching: false,
        error: null,
        totalCount: payload.totalCount,
        summary: payload.summary,
      };
    });

    builder.addCase(fetchOverdueUnpaidInvoicesWidget.rejected, (state, { error }) => {
      state.overdueUnpaidInvoices = {
        isFetching: false,
        error,
        totalCount: null,
        summary: undefined,
      };
    });

    builder.addCase(fetchSettledPaymentsWidget.pending, state => {
      state.settledPayments = {
        ...state.settledPayments,
        isFetching: true,
        error: null,
      };
    });

    builder.addCase(fetchSettledPaymentsWidget.fulfilled, (state, { payload }) => {
      state.settledPayments = {
        isFetching: false,
        error: null,
        totalCount: payload.totalCount,
        summaryByCurrency: payload.summaryByCurrency,
      };
    });

    builder.addCase(fetchSettledPaymentsWidget.rejected, (state, { error }) => {
      state.settledPayments = {
        ...state.settledPayments,
        isFetching: false,
        error,
        totalCount: null,
        summaryByCurrency: {},
      };
    });

    builder.addCase(fetchUnmappedBankFilePaymentsWidget.pending, state => {
      state.unmappedBankFilePayments = {
        ...state.unmappedBankFilePayments,
        isFetching: true,
        error: null,
      };
    });

    builder.addCase(fetchUnmappedBankFilePaymentsWidget.fulfilled, (state, { payload }) => {
      state.unmappedBankFilePayments = {
        isFetching: false,
        error: null,
        totalCount: payload.totalCount,
        summary: payload.summary,
      };
    });

    builder.addCase(fetchUnmappedBankFilePaymentsWidget.rejected, (state, { error }) => {
      state.unmappedBankFilePayments = {
        isFetching: false,
        error,
        totalCount: null,
        summary: [],
      };
    });
  },
});

export const {
  actions: {
    resetOverdueUnpaidInvoicesWidgetError,
    resetSettledPaymentsWidgetError,
    resetUnmappedBankFilePaymentsWidgetError,
  },
  reducer: dashboardWidgetsReducer,
} = dashboardWidgetsSlice;

export const selectOverdueUnpaidInvoicesWidget = (state: MerchantAdminState) =>
  state.dashboardWidgets.overdueUnpaidInvoices;

export const selectSettledPaymentsWidget = (state: MerchantAdminState) => state.dashboardWidgets.settledPayments;

export const selectUnmappedBankFilePaymentsWidget = (state: MerchantAdminState) =>
  state.dashboardWidgets.unmappedBankFilePayments;
